import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, model, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { Select, SelectModule } from 'primeng/select';
import { ColumnService } from '../../../@core/services/column.service';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { EntityPropertyDropdownComponent, PrefixOptions } from '../entity-property-dropdown/entity-property-dropdown.component';

const non_object_types = ['string', 'number', 'boolean', 'datetime', 'date', 'float', 'json', 'int', 'numeric'];

@Component({
  selector: 'app-entity-property-chooser',
  imports: [
    EntityPropertyDropdownComponent,
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TranslatePipe,
  ],
  templateUrl: './entity-property-chooser.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityPropertyChooserComponent extends FieldType<FieldTypeConfig> implements AfterViewInit, ControlValueAccessor {
  entityName = model<string>('');

  entityOptions = signal<(PrefixOptions & { type: string })[]>([]);
  availableOptions = computed(() => {
    let options = this.entityOptions();
    let chosenEntities = this.chosenEntities();
    return options.filter(x => !chosenEntities.includes(x.fieldName))
      .sort((a, b) => a.fieldName == "customFields" ? -1 : b.fieldName == "customFields" ? 1 : a.label.localeCompare(b.label)); //customFields should be at the start
  });

  items = model<{ [fieldName: string]: string[] }>({});
  chosenEntities = signal<string[]>([]); /* computed(() => {
    let items = this.items();
    return Object.keys(items).filter(x => x !== this.entityName());
  }); */
  value = model<string[]>([]);

  disabled = model<boolean>(false);

  isInitialized = false;
  hasItems = () => Object.values({ ...this.items(), [this.entityName()]: [] }).some(x => x.length > 0);
  private readonly destroyRef = inject(DestroyRef);
  constructor(
    private columnService: ColumnService,
    private i18nService: I18nService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.props?.entityName)
      this.entityName.set(this.props.entityName);

    // valueChanges must be torn down; recreating the field stacked listeners otherwise.
    this.formControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (this.value() !== value) {
          if (this.isInitialized) {
            // let emptyItems = Object.keys(this.items()).filter(x => this.items()[x].length > 0);
            let result = this.parseValue(value ?? []);
            this.chosenEntities.set(Object.keys(result));
            this.items.set(result);
          }
          this.value.set(value);
        }
      });
    await this.loadData();
  }

  async loadData() {
    let columns = await this.columnService.getAllColumnsOfType(this.entityName(), false);
    let candidates = columns.filter(x => x.entityType == this.entityName() && x.dataType && !non_object_types.includes(x.dataType.toLowerCase()) && !x.isEnum && !x.isArray /* && (x.prop ?? x.field) != "customFields" */);
    let entities: (PrefixOptions & { type: string })[] = candidates.map(x => ({
      type: x.dataType,
      label: this.i18nService.translate(this.entityName() + '.' + (x.prop ?? x.field)),
      fieldName: x.prop ?? x.field
    })).filter(x => x.type !== null);
    this.entityOptions.set(entities);

    let value = this.parseValue();
    this.chosenEntities.set(Object.keys(value));
    this.items.set(value);
    this.isInitialized = true;
    this.cdr.detectChanges();
  }

  private parseValue(value: string[] = undefined): { [entityName: string]: string[] } {
    if (!value)
      value = this.value();
    let items: { [entityName: string]: string[] } = {
      [this.entityName()]: []
    };

    for (let field of value ?? []) {
      if (field.includes('.')) {
        let parts = field.split('.');
        let entityName = parts[0];
        let propertyName = parts.slice(1).join('.');
        if (!items[entityName])
          items[entityName] = [];
        items[entityName].push(propertyName);
      }
      else
        items[this.entityName()].push(field);
    }

    return items;
  }

  newEntityChosen(entityOption: (PrefixOptions & { type: string }), selectComponent: Select) {
    if (!entityOption?.fieldName)
      return;
    this.addEntity(entityOption);
    setTimeout(() => {
      selectComponent.hide();
      selectComponent.clear();
      this.cdr.detectChanges();
    }, 0);
  }
  addEntity(entityOption: (PrefixOptions & { type: string })) {
    if (!entityOption?.fieldName)
      return;
    this.items()[entityOption.fieldName] = [];
    this.chosenEntities.set([...this.chosenEntities(), entityOption.fieldName]);
    this.cdr.detectChanges();
  }

  getOptionForFieldName(fieldName: string): (PrefixOptions & { type: string }) {
    if (fieldName === this.entityName())
      return null;
    return this.entityOptions().find(x => x.fieldName === fieldName);
  }

  removeEntity(entityOption: (PrefixOptions & { type: string })) {
    delete this.items()[entityOption.fieldName];
    this.chosenEntities.set(this.chosenEntities().filter(x => x !== entityOption.fieldName));
    this.updateValue();
  }

  updateValue() {
    let items = this.items();
    let result: string[] = [];
    for (let key of Object.keys(items)) {
      if (key == this.entityName())
        result.push(...items[key]);
      else
        result.push(...items[key].map(x => key + '.' + x));
    }
    this.formControl?.setValue(result);
    this.onChange?.(result);
  }

  entityValueChanged(entityName: string, value: string[]) {
    if (!this.isInitialized)
      return;
    this.items()[entityName] = value;
    this.updateValue();
  }

  //#region ControlValueAccessor
  onChange: (value: string[]) => void;
  onTouched: () => void;

  writeValue(obj: string[]): void {
    if (this.formControl)
      this.formControl?.setValue(obj);
    else {
      this.value.set(obj);
      this.items.set(this.parseValue(obj));
    }
  }
  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled)
      this.formControl?.disable();
    else
      this.formControl?.enable();
  }
  //#endregion
}
